import React, { useState, useEffect, useRef } from 'react';
import { useTranslate } from 'react-translate';

import { ReportStatus } from "./../Button/Downloader/Reports/Report.js";
import { loadProjectAsync } from '../Session/SessionLoader.js';

import { Session } from "./../Session/Session.js";
import { DelayedViewer } from '../DelayedViewer';
import { Loading } from '../Loading';

import './Authenticator.css';
import PrivacyPolicy from './PrivacyPolicy';

import { NotificationContextHandler } from '../Notifications/NotificationContextHandler.js'

const AuthState = {
    LOADING: 0,
    AUTO_AUTHENTICATING: 1,
    USERCONSENTREQUESTED: 2,
    AUTHENTICATING: 3,
    AUTHENTICATED: 4,
    ERROR: 5
};

export function Authenticator(props) {
    const t = useTranslate("session");
    const at = useTranslate("authenticator");
    const et = useTranslate("error");
    const pt = useTranslate("privacyPolicy");

    const [errorMessage, setErrorMessage] = useState(null);

    const [privacyPolicyVisible, setPrivacyPolicyVisible] = useState(false);

    const [authState, setAuthState] = useState(AuthState.AUTO_AUTHENTICATING);

    const [projectData, setProjectData] = useState(null);

    useEffect(() => {
        /* Only call the authenticate function when we are loading */
        if (authState === AuthState.AUTO_AUTHENTICATING) {
            authenticate();
        }
    });

    const togglePrivacyPolicy = () => {
        setPrivacyPolicyVisible(!privacyPolicyVisible);
    };

    const passwordInputRef = useRef(null);

    function userConsented() {
        authenticate(false, true)
    }

    function usePasswordFunction() {
        authenticate(true, true)
    }

    /**
     * @description Function that tries to authenticate the user in order to display the souvenir's data
     * @param { Boolean } usePassword
     */
    function authenticate(usePassword = false, consent = false) {
        /* Early stages of loading may not provide a projectInfo */
        if (props.projectInfo === undefined || props.projectInfo === null) {
            return;
        }

        let password = usePassword
            ? passwordInputRef?.current?.value : null;

        setErrorMessage(null);
        setAuthState(AuthState.LOADING);

        loadProjectAsync(props.projectInfo, password, consent).then(projectReport => {
            switch (projectReport.status) {
                case ReportStatus.OK:
                    setAuthState(AuthState.AUTHENTICATED);
                    setProjectData(projectReport.data);

                    break;
                case ReportStatus.NOCONTENT:
                    setAuthState(AuthState.ERROR);
                    setErrorMessage('expired-project');

                    break;
                case ReportStatus.USERCONSENTREQUESTED:
                    setAuthState(AuthState.USERCONSENTREQUESTED);

                    break;
                case ReportStatus.PASSWORDREQUESTED:
                    setAuthState(AuthState.AUTHENTICATING);

                    if (usePassword) {
                        setErrorMessage("unauthorized-access");
                    }

                    break;
                case ReportStatus.NOTFOUND:
                    setAuthState(AuthState.ERROR);
                    setErrorMessage('project-not-found');

                    break;
                case ReportStatus.NOTACCEPTABLE:
                    setAuthState(AuthState.ERROR);
                    setErrorMessage('invalid-id');

                    break;
                case ReportStatus.GONE:
                    setAuthState(AuthState.ERROR);
                    setErrorMessage('invalid-project-data');

                    break;
                default:
                    setAuthState(AuthState.ERROR);
                    setErrorMessage('generic');

                    break;
            }
        })
    }

    let renderedState = (<></>);

    /* Switch the state to determine which view to show (loading, auth, session or error) */
    switch (authState) {
        /* When the we are trying to fetch the project data */
        case AuthState.LOADING:
        case AuthState.AUTO_AUTHENTICATING:
            renderedState = (
                <DelayedViewer show={true} delay={2000}>
                    <Loading />
                </DelayedViewer>
            );

            break;
        /* When the user needs to input his password */
        case AuthState.USERCONSENTREQUESTED:
            renderedState = (
                <div className="auth-container" style={{ maxWidth: privacyPolicyVisible ? '555px' : '365px', minWidth: '210px' }}>
                    {
                        privacyPolicyVisible
                            ? (<>
                                    <h1 className="auth-title" style={{ color: `#${props.primaryColor}` }}>{pt("title")}</h1>
                                    <PrivacyPolicy />
                                    <AcceptButton className="auth-button-margin-auto" authenticate={userConsented} primaryColor={props.primaryColor} />
                                </>)
                            : ( // La div initiale à afficher
                                <>
                                    <h1 className="auth-title" style={{ color: `#${props.primaryColor}` }}>{pt("documentsReady")}</h1>
                                    <p style={{ margin: 'auto 16px' }}>{pt("pageConsulting")}<span style={{ textDecoration: 'underline', color: 'blue', cursor: 'pointer' }} onClick={togglePrivacyPolicy}>{pt("privacyPolicy")}</span></p>
                                    <AcceptButton authenticate={userConsented} primaryColor={props.primaryColor} />
                                </>)
                    }
                </div>)
            break;
        /* When the user needs to input his password */
        case AuthState.AUTHENTICATING:
            renderedState = (
                <div className="auth-container" style={{ maxWidth:'70%' }}>
                    <h1
                        className="auth-title"
                        style={{
                            color: `#${props.primaryColor}`
                        }}>
                        {at("title")}
                    </h1>
                    <h4
                        className="auth-title"
                        style={{
                            color: `#${props.primaryColor}`
                        }}>
                        {at("subtitle")}
                    </h4>
                    <input
                        ref={passwordInputRef}
                        className="auth-input"
                        type="number"
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                authenticate(true);
                            }
                        }}
                        style={{
                            color: `#${props.primaryColor}`,
                            borderColor: `#${props.primaryColor}`,
                        }}
                        autoFocus />
                    {errorMessage !== null &&
                        <h5 className="auth-error">
                            {et(errorMessage)}
                        </h5>
                    }
                    <AuthenticateButton authenticate={usePasswordFunction} primaryColor={props.primaryColor} />
                </div>
            )
            break;
        /* Successful authentication */
        case AuthState.AUTHENTICATED:
            renderedState = (
                <NotificationContextHandler>
                    <Session projectData={projectData} error={null} />
                </NotificationContextHandler>
            );
            break;
        /* Error (mostly due to invalid link) */
        case AuthState.ERROR:
        default:
            renderedState = (
                <div className="error">
                    <h1>{t('errorTitle')}</h1>
                    <h3>{et(errorMessage)}</h3>
                </div>
            );
            break;
    }

    return (
        <div className="main-container">
            { renderedState }
        </div>
    );
}

function AuthenticateButton(props) {
    const at = useTranslate("authenticator");
    const [hovering, setHovering] = useState(false);

    return (
        <button
            className="auth-button"
            onClick={() => props.authenticate(true)}
            style={{
                color: hovering ? 'white' : `#${props.primaryColor}`,
                borderColor: `#${props.primaryColor}`,
                backgroundColor: hovering ? `#${props.primaryColor}` : 'transparent'
            }}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}>
            {at("button")}
        </button>
    );
}

function AcceptButton(props) {
    const pt = useTranslate("privacyPolicy");
    const [hovering, setHovering] = useState(false);

    return (
        <button
            className="auth-button {props?.className}"
            onClick={() => props.authenticate(true)}
            style={{
                color: props.disabled ? 'grey' : (hovering ? 'white' : `#${props.primaryColor}`),
                borderColor: props.disabled ? 'grey' : `#${props.primaryColor}`,
                backgroundColor: props.disabled ? 'lightgrey' : (hovering ? `#${props.primaryColor}` : 'transparent'),
                cursor: props.disabled ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={() => !props.disabled && setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            disabled={props.disabled}>
            {pt("validate")}
        </button>
    );
}